import { useEffect, useRef, useCallback } from 'react';
import { useFormContext } from '@/components/form/FormContext';

type ValidationRule = (value: any) => string;

interface UseFormFieldOptions {
    name: string;
    value: any;
    label?: string;
    rules?: ValidationRule[];
    validateOn?: 'blur' | 'change' | 'both';
    onChange?: (value: any) => void;
}

export const useFormField = ({
    name,
    value,
    label,
    rules = [],
    validateOn = 'both',
    onChange
}: UseFormFieldOptions) => {
    const context = useFormContext();

    // Stable refs
    const rulesRef = useRef(rules);
    const isRegisteredRef = useRef(false);
    const prevValueRef = useRef(value);
    const onChangeRef = useRef(onChange);
    const valueRef = useRef(value);

    // Update refs without triggering effects
    useEffect(() => {
        rulesRef.current = rules;
    }, [rules]);

    useEffect(() => {
        onChangeRef.current = onChange;
    }, [onChange]);

    useEffect(() => {
        valueRef.current = value;
    }, [value]);

    // Single registration effect - only on mount/unmount or name change
    useEffect(() => {
        // Register field on mount
        context.registerField(name, value, rulesRef.current, label);
        isRegisteredRef.current = true;

        // Cleanup on unmount or name change
        return () => {
            context.unregisterField(name);
            isRegisteredRef.current = false;
        };
    }, [name, label, context.registerField, context.unregisterField]);

    useEffect(() => {
        if (isRegisteredRef.current) {
            context.registerField(name, value, rules, label);
        }
    }, [rules, name, label, context.registerField]);

    useEffect(() => {
        // Skip if not registered yet
        if (!isRegisteredRef.current) return;

        // Update field value in context
        context.updateFieldValue(name, value);

        // Check if this is a value change (not initial mount)
        const valueChanged = prevValueRef.current !== value;
        prevValueRef.current = value;

        if (!valueChanged) return;

        // Determine if we should validate on this change
        const isTouched = context.touched?.[name];
        const hasAttemptedSubmit = context.attemptedSubmit;
        const shouldValidateOnChange = validateOn === 'change' || validateOn === 'both';

        // Validate immediately if conditions are met
        if ((isTouched || hasAttemptedSubmit) && shouldValidateOnChange) {
            // Use handleChange which validates immediately
            context.handleChange?.(name, value, rulesRef.current);
        }
    }, [value, name, validateOn, context]);

    // Memoized blur handler - validates and marks as touched
    const handleBlur = useCallback(() => {
        if (validateOn === 'blur' || validateOn === 'both') {
            context.handleBlur?.(name, valueRef.current, rulesRef.current);
        }
    }, [name, validateOn, context]);

    // Memoized change handler - calls custom onChange and context validation
    const handleChange = useCallback(
        (newValue: any) => {
            // Call custom onChange first
            onChangeRef.current?.(newValue);

            if (validateOn === 'change' || validateOn === 'both') {
                context.handleChange?.(name, newValue, rulesRef.current);
            }
        },
        [name, validateOn, context]
    );

    // Get current error and touched state from context
    const error = context.getFieldError(name) || '';
    const touched = context.touched?.[name] || false;
    const showError = touched && !!error;

    return {
        error,
        touched,
        handleBlur,
        handleChange,
        showError
    };
};

export default useFormField;
